import { REMINDER_SCOPES, type ReminderBucket, type ReminderScope } from '@hr/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BellRingIcon, BriefcaseIcon, UsersIcon } from 'lucide-react';
import { z } from 'zod';
import { EmptyState, PageHeader } from '@/components/page';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { bucketOf } from '@/features/reminders/due';
import { ReminderItem } from '@/features/reminders/reminder-ui';
import type { ReminderRow } from '@/features/reminders/use-reminders';
import { uk } from '@/i18n/uk';
import { trpc } from '@/lib/trpc';
import { isHr } from '@/lib/roles';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  tab: z.enum(REMINDER_SCOPES).optional().catch(undefined),
  owner: z.enum(['mine', 'all']).optional().catch(undefined),
  status: z.enum(['active', 'done']).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/reminders')({
  validateSearch: searchSchema,
  component: RemindersPage,
});

const BUCKETS: ReminderBucket[] = ['overdue', 'today', 'tomorrow', 'week', 'later'];
const BUCKET_HEADING: Record<ReminderBucket, string> = {
  overdue: 'text-rose-700',
  today: 'text-amber-800',
  tomorrow: 'text-sky-700',
  week: 'text-muted-foreground',
  later: 'text-muted-foreground',
};

function RemindersPage() {
  const t = uk.reminders;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const scope: ReminderScope = search.tab ?? 'contest';
  const owner = search.owner ?? 'mine';
  const status = search.status ?? 'active';
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery(trpc.reminders.list.queryOptions({ scope, owner, status }));
  const counts = useQuery(trpc.reminders.dueCounts.queryOptions());

  return (
    <>
      <PageHeader title={t.title} description={t.description} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={(v) => setSearch({ tab: v as ReminderScope })}>
          <TabsList>
            <TabsTrigger value="contest">
              <BriefcaseIcon /> {t.contests}
              <DueCount n={counts.data?.contest} />
            </TabsTrigger>
            <TabsTrigger value="candidate">
              <UsersIcon /> {t.candidates}
              <DueCount n={counts.data?.candidate} />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Segmented
          value={owner}
          options={[
            ['mine', t.mine],
            ['all', t.all],
          ]}
          onChange={(v) => setSearch({ owner: v === 'mine' ? undefined : v })}
        />
        <Segmented
          value={status}
          options={[
            ['active', t.active],
            ['done', t.done],
          ]}
          onChange={(v) => setSearch({ status: v === 'active' ? undefined : v })}
        />
      </div>

      {list.isPending ? (
        <Skeleton className="h-48 max-w-3xl" />
      ) : !list.data?.length ? (
        <EmptyState
          icon={BellRingIcon}
          title={status === 'active' ? t.emptyActive : t.emptyDone}
          hint={scope === 'contest' ? t.emptyContestHint : t.emptyCandidateHint}
        />
      ) : status === 'done' ? (
        <ul className="max-w-3xl space-y-1.5">
          {list.data.map((r) => (
            <ReminderItem
              key={r.id}
              reminder={r}
              scope={scope}
              canWrite={isHr(me.role)}
              meId={me.id}
              showTarget
            />
          ))}
        </ul>
      ) : (
        <div className="max-w-3xl space-y-6">
          {BUCKETS.map((bucket) => {
            const items = list.data.filter((r) => bucketOf(r) === bucket);
            if (!items.length) return null;
            return (
              <Group
                key={bucket}
                bucket={bucket}
                items={items}
                scope={scope}
                canWrite={isHr(me.role)}
                meId={me.id}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

function Group({
  bucket,
  items,
  scope,
  canWrite,
  meId,
}: {
  bucket: ReminderBucket;
  items: ReminderRow[];
  scope: ReminderScope;
  canWrite: boolean;
  meId: string;
}) {
  return (
    <section className="space-y-2">
      <h2 className={cn('flex items-center gap-2 text-sm font-semibold', BUCKET_HEADING[bucket])}>
        {uk.reminders.buckets[bucket]}
        <span className="rounded-full bg-muted px-2 text-xs font-normal text-muted-foreground">
          {items.length}
        </span>
      </h2>
      <ul className="space-y-1.5">
        {items.map((r) => (
          <ReminderItem
            key={r.id}
            reminder={r}
            scope={scope}
            canWrite={canWrite}
            meId={meId}
            showTarget
          />
        ))}
      </ul>
    </section>
  );
}

/** Кількість нагадувань на сьогодні й прострочених на вкладці. */
function DueCount({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <span className="rounded-full bg-rose-600 px-1.5 text-xs text-white tabular-nums">{n}</span>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: [T, string][];
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex rounded-md border p-0.5" role="group">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-2.5 py-1 text-sm transition-colors',
            value === v
              ? 'bg-secondary font-medium'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
